import type { ReportTag } from "@features/report/tag/types/reportTag";

export interface ReportTagAvailableActions {
    showEdit: boolean;
    showRequestReopen: boolean;
    showApprove: boolean;
    showDeny: boolean;
}

const CLOSED = "CLOSED";

/**
 * Returns which actions are available for a report tag based on status and
 * reopen request state. Use this for declarative button visibility.
 *
 * Rules:
 * - Closed + isReopenRequested: show Approve & Deny (and Tick/X); hide Edit & Request Reopen.
 * - Closed + !isReopenRequested: show Edit (if allowed) & Request Reopen; hide Approve & Deny.
 * - Not closed: normal actions (Edit only; no reopen flow).
 */
export function getAvailableTagActions(tag: ReportTag | null | undefined): ReportTagAvailableActions {
    if (!tag) {
        return { showEdit: false, showRequestReopen: false, showApprove: false, showDeny: false };
    }

    const statusName = tag.report_tag_status_name;
    const isClosed = (statusName ?? "").toUpperCase() === CLOSED;
    const isReopenRequested = Boolean(tag.is_reopen_requested ?? tag.approval_requested);

    if (isClosed) {
        if (isReopenRequested) {
            return {
                showEdit: false,
                showRequestReopen: false,
                showApprove: true,
                showDeny: true,
            };
        }
        return {
            showEdit: true,
            showRequestReopen: true,
            showApprove: false,
            showDeny: false,
        };
    }

    return {
        showEdit: true,
        showRequestReopen: false,
        showApprove: false,
        showDeny: false,
    };
}
